import React from "react";
import {
  UserPlus,
  ShieldCheck,
  CheckCircle2,
  Send,
} from "lucide-react";

const steps = [
  {
    id: 1,
    title: "Register",
    description: "Name, phone, city. Two fields more.",
    icon: <UserPlus size={20} className="text-[var(--hero-green)]" strokeWidth={2.2} />,
  },
  {
    id: 2,
    title: "KYC",
    description: "PAN and bank account, verified once.",
    icon: <ShieldCheck size={20} className="text-[var(--hero-green)]" strokeWidth={2.2} />,
  },
  {
    id: 3,
    title: "Get approved",
    description: "Usually within one working day.",
    icon: <CheckCircle2 size={20} className="text-[var(--hero-green)]" strokeWidth={2.2} />,
  },
  {
    id: 4,
    title: "Start submitting",
    description: "Your first lead can go in the same day.",
    icon: <Send size={19} className="text-[var(--hero-green)]" strokeWidth={2.2} />,
  },
];

function Onboarding() {
  return (
    <section className="relative overflow-hidden bg-white py-16 sm:py-20 lg:py-24">
      {/* Soft Ambient Radial Background Glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(46,229,162,0.05),transparent_50%)]"
      />

      <div className="section-container relative z-10">
        
        {/* ================= SECTION HEADER ================= */}
        <div className="mx-auto max-w-[700px] text-center">
          <p className="font-jakarta text-[11px] font-medium tracking-[0.34em] text-[var(--hero-green)] sm:text-[12px] uppercase mb-2.5">
            ONBOARDING PROCESS
          </p>
          <h2 className="font-bricolage text-[30px] sm:text-[36px] md:text-[40px] font-bold leading-[1.12] tracking-[-1px] text-[#111827]">
            Earn More With <span className="text-[var(--hero-green)]">Every Step</span>
          </h2>
          <p className="mt-3 font-jakarta text-[14.5px] sm:text-[16px] leading-[1.6] text-[#556369]">
            A simple and rewarding onboarding process to maximize your earnings.
          </p>
        </div>

        {/* ================= DESKTOP HORIZONTAL 4-STEP FLOW ================= */}
        <div className="relative mt-12 sm:mt-14 lg:mt-16 hidden md:block">
          
          {/* Continuous Animated Connecting SVG Line (Exactly from center of Node 1 to Node 4) */}
          <div className="absolute top-[32px] lg:top-[34px] left-[12.5%] right-[12.5%] -translate-y-1/2 z-0">
            <svg
              className="w-full h-[6px] overflow-visible"
              viewBox="0 0 1000 6"
              fill="none"
              preserveAspectRatio="none"
            >
              {/* Background static guide line */}
              <line
                x1="0"
                y1="3"
                x2="1000"
                y2="3"
                stroke="#e2eae5"
                strokeWidth="2"
                strokeDasharray="5 5"
              />

              {/* Foreground Animated Continuous Flowing Line */}
              <line
                x1="0"
                y1="3"
                x2="1000"
                y2="3"
                stroke="#008751"
                strokeWidth="2"
                strokeDasharray="5 5"
                strokeLinecap="round"
                className="step-flow-line"
              />
            </svg>
          </div>

          {/* 4 Step Nodes in Symmetrical Grid */}
          <div className="grid grid-cols-4 gap-4 lg:gap-6 relative z-10">
            {steps.map((step) => (
              <div key={step.id} className="flex flex-col items-center text-center group">
                
                {/* Sleek Compact Circle Node */}
                <div className="relative flex h-[64px] w-[64px] lg:h-[68px] lg:w-[68px] shrink-0 aspect-square items-center justify-center rounded-full bg-white border border-[#d6e8dd] shadow-[0_6px_20px_rgba(0,0,0,0.04)] transition-all duration-300 group-hover:scale-108 group-hover:border-[var(--hero-green)] group-hover:shadow-[0_12px_26px_rgba(0,135,81,0.14)]">
                  
                  {/* Subtle Hover Glow */}
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 rounded-full bg-[var(--hero-green)]/10 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                  />

                  {/* Icon */}
                  <div className="relative z-10 transition-transform duration-300 group-hover:scale-105">
                    {step.icon}
                  </div>
                </div>

                {/* Step Title */}
                <h3 className="mt-3.5 font-bricolage text-[16px] lg:text-[17px] font-bold tracking-tight text-[#111827] group-hover:text-[var(--hero-green)] transition-colors duration-200">
                  {step.title}
                </h3>

                {/* Step Description */}
                <p className="mt-1 font-jakarta text-[12.5px] leading-[1.5] text-[#556369] max-w-[170px]">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* ================= MOBILE & TABLET EXPANDED SPACIOUS STEP FLOW ================= */}
        <div className="mt-12 sm:mt-16 space-y-0 md:hidden relative max-w-[460px] mx-auto px-3 sm:px-4">
          {steps.map((step, idx) => (
            <div
              key={step.id}
              className="relative flex items-start gap-5 pb-16 sm:pb-20 last:pb-0"
            >
              {/* Connector Line strictly between current step and next step (Zero Overshoot) */}
              {idx < steps.length - 1 && (
                <div className="absolute left-[31px] top-[64px] bottom-0 w-[3px] -translate-x-1/2 z-0 overflow-hidden pointer-events-none">
                  <svg
                    className="w-full h-full"
                    viewBox="0 0 3 100"
                    preserveAspectRatio="none"
                  >
                    {/* Background guide line */}
                    <line
                      x1="1.5"
                      y1="0"
                      x2="1.5"
                      y2="100"
                      stroke="#e2eae5"
                      strokeWidth="2"
                      strokeDasharray="6 6"
                      vectorEffect="non-scaling-stroke"
                    />
                    {/* Flowing animated green line */}
                    <line
                      x1="1.5"
                      y1="0"
                      x2="1.5"
                      y2="100"
                      stroke="#008751"
                      strokeWidth="2.5"
                      strokeDasharray="6 6"
                      vectorEffect="non-scaling-stroke"
                      className="step-flow-line-vert"
                    />
                  </svg>
                </div>
              )}

              {/* Icon Circle (Guaranteed 1:1 Aspect Ratio) */}
              <div className="relative z-10 flex h-[62px] w-[62px] shrink-0 aspect-square items-center justify-center rounded-[20px] bg-white border border-[#d6e8dd] shadow-[0_8px_24px_rgba(0,135,81,0.09)]">
                {step.icon}
                {/* Step number badge */}
                <span className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-[var(--hero-green)] text-[10px] font-bold font-jakarta text-white shadow-xs">
                  {step.id}
                </span>
              </div>

              {/* Text Info */}
              <div className="pt-2 min-w-0">
                <h3 className="font-bricolage text-[17px] sm:text-[18px] font-bold text-[#111827]">
                  {step.title}
                </h3>
                <p className="mt-1 font-jakarta text-[13.5px] sm:text-[14px] leading-[1.55] text-[#556369]">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>

      </div>

      {/* Line Flow Animation Keyframes */}
      <style>{`
        @keyframes flowHorizontal {
          from {
            stroke-dashoffset: 40;
          }
          to {
            stroke-dashoffset: 0;
          }
        }
        @keyframes flowVertical {
          from {
            stroke-dashoffset: 40;
          }
          to {
            stroke-dashoffset: 0;
          }
        }
        .step-flow-line {
          animation: flowHorizontal 1.8s linear infinite;
        }
        .step-flow-line-vert {
          animation: flowVertical 1.8s linear infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .step-flow-line,
          .step-flow-line-vert {
            animation: none;
          }
        }
      `}</style>
    </section>
  );
}

export default Onboarding;
